import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  catchError,
  filter,
  forkJoin,
  map,
  mergeMap,
  Observable,
  of,
  switchMap,
  withLatestFrom,
} from 'rxjs';

import { selectSelectedSubscription } from '@stores/console';
import { AgentsActions } from './agents.actions';
import { AgentService } from './api/agent.service';
import {
  AgentDto,
  AgentStatusType,
  CreateUpdateAgentInstructionPayload,
} from './models/agent.model';
import { GcsUploadService } from '@core/api/gcs-upload.service';
import { selectSelectedAgent } from '@stores/agents/agents.selectors';

export class AgentsEffects {
  private readonly actions$ = inject(Actions);
  createVersionAfterInstructionCreate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AgentsActions.createInstructionSuccess),
      map(({ agentInstruction, createVersion }) => {
        const shouldCreateVersion = createVersion === true;
        return shouldCreateVersion
          ? AgentsActions.createAgentVersion({
              tenantUuid: agentInstruction.tenantUuid,
              agentUuid: agentInstruction.agentUuid,
            })
          : AgentsActions.getAgentDetail({
              tenantUuid: agentInstruction.tenantUuid,
              agentUuid: agentInstruction.agentUuid,
            });
      }),
    ),
  );
  getAgentVersionsAfterChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        AgentsActions.createAgentVersionSuccess,
        AgentsActions.updateAgentVersionStatusSuccess,
        AgentsActions.createAgentSuccess,
        AgentsActions.updateAgentEverythingSuccess,
      ),
      filter((action: any) => {
        if (action.type === AgentsActions.updateAgentEverythingSuccess.type) {
          return action.createVersion === true;
        }
        return true;
      }),
      map((action: any) => {
        const payload = action.agent || action;
        return AgentsActions.getAgentVersions({
          tenantUuid: payload.tenantUuid,
          agentUuid: payload.agentUuid,
        });
      }),
    ),
  );
  refreshAgentAfterVersionCreate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AgentsActions.createAgentVersionSuccess),
      map(({ tenantUuid, agentUuid }) => AgentsActions.getAgentDetail({ tenantUuid, agentUuid })),
    ),
  );
  refreshAgentAfterVersionStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AgentsActions.updateAgentVersionStatusSuccess),
      map(({ tenantUuid, agentUuid }) => AgentsActions.getAgentDetail({ tenantUuid, agentUuid })),
    ),
  );
  private readonly svc = inject(AgentService);
  getAgents$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AgentsActions.getAgents),
      switchMap(({ tenantUuid, subscriptionUuid }) =>
        this.svc.getAgents(tenantUuid).pipe(
          map((agents) => {
            const agentBySubscriptionUuid = [
              ...agents.filter(
                (a) =>
                  a.subscriptionUuid === subscriptionUuid &&
                  a.agentStatusType !== AgentStatusType.DELETED,
              ),
            ];
            return AgentsActions.loadSuccess({ agents: agentBySubscriptionUuid });
          }),
          catchError((error) => of(AgentsActions.loadFailure({ error }))),
        ),
      ),
    ),
  );
  getAgentHubs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AgentsActions.getAgentHubs),
      switchMap(({ tenantUuid }) =>
        this.svc.getAgentHubs(tenantUuid).pipe(
          map((agentHubs) => {
            return AgentsActions.getAgentHubsSuccess({ agentHubs });
          }),
          catchError((error) => of(AgentsActions.getAgentHubsFailure({ error }))),
        ),
      ),
    ),
  );
  getAgentDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AgentsActions.getAgentDetail),
      switchMap(({ tenantUuid, agentUuid }) =>
        this.svc.getAgentDetail(tenantUuid, agentUuid).pipe(
          map((agent) => AgentsActions.getAgentSuccess({ agent })),
          catchError((error) => of(AgentsActions.getAgentFailure({ error }))),
        ),
      ),
    ),
  );
  getInstructionDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AgentsActions.getInstructionDetail),
      switchMap(({ tenantUuid, agentUuid }) =>
        this.svc.getAgentInstruction(tenantUuid, agentUuid).pipe(
          map((agentInstructions) => AgentsActions.getInstructionSuccess({ agentInstructions })),
          catchError((error) => of(AgentsActions.getInstructionFailure({ error }))),
        ),
      ),
    ),
  );
  getPrompts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AgentsActions.getPrompts),
      switchMap(({ tenantUuid, agentUuid }) =>
        this.svc.getAgentPrompts(tenantUuid, agentUuid).pipe(
          map((agentPrompts) => AgentsActions.getPromptsSuccess({ agentPrompts })),
          catchError((error) => of(AgentsActions.getPromptsFailure({ error }))),
        ),
      ),
    ),
  );
  updatePrompt$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AgentsActions.updatePrompt),
      mergeMap(({ tenantUuid, agentUuid, agentPromptId, changes }) =>
        this.svc.updateAgentPrompts(tenantUuid, agentUuid, agentPromptId, changes).pipe(
          map((agentPrompt) => AgentsActions.updatePromptSuccess({ agentPrompt })),
          catchError((error) => of(AgentsActions.updatePromptFailure({ error }))),
        ),
      ),
    ),
  );

  createPrompt$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AgentsActions.createPrompt),
      mergeMap(({ tenantUuid, agentUuid, changes }) =>
        this.svc.createAgentPrompts(tenantUuid, agentUuid, changes).pipe(
          map((agentPrompt) => AgentsActions.createPromptSuccess({ agentPrompt })),
          catchError((error) => of(AgentsActions.createPromptFailure({ error }))),
        ),
      ),
    ),
  );

  getAgentInputs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AgentsActions.getAgentInputs),
      switchMap(({ tenantUuid, agentUuid }) =>
        this.svc.getAgentInputs(tenantUuid, agentUuid).pipe(
          map((agentInputs) => AgentsActions.getAgentInputsSuccess({ agentInputs })),
          catchError((error) => of(AgentsActions.getAgentInputsFailure({ error }))),
        ),
      ),
    ),
  );

  createAgentInputs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AgentsActions.createAgentInput),
      mergeMap(({ tenantUuid, agentUuid, changes }) =>
        this.svc.createAgentInputs(tenantUuid, agentUuid, changes).pipe(
          map((agentInputs) => AgentsActions.createAgentInputSuccess({ agentInputs })),
          catchError((error) => of(AgentsActions.createAgentInputFailure({ error }))),
        ),
      ),
    ),
  );

  updateAgentInput$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AgentsActions.updateAgentInput),
      mergeMap(({ tenantUuid, agentUuid, agentInputId, changes }) =>
        this.svc.updateAgentInput(tenantUuid, agentUuid, agentInputId, changes).pipe(
          map((agentInput) => AgentsActions.updateAgentInputSuccess({ agentInput })),
          catchError((error) => of(AgentsActions.updateAgentInputFailure({ error }))),
        ),
      ),
    ),
  );

  deleteAgentInput$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AgentsActions.deleteAgentInput),
      mergeMap(({ tenantUuid, agentUuid, agentInputId }) =>
        this.svc.deleteAgentInput(tenantUuid, agentUuid, agentInputId).pipe(
          map((agent) => {
            return AgentsActions.deleteAgentInputSuccess({ agentInputId });
          }),
          catchError((error) => of(AgentsActions.deleteAgentInputFailure({ error }))),
        ),
      ),
    ),
  );

  updateAgent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AgentsActions.updateAgent),
      switchMap(({ tenantUuid, agentUuid, changes, createVersion }) =>
        this.svc.updateAgent(tenantUuid, agentUuid, changes).pipe(
          mergeMap((agent) => {
            const shouldCreateVersion = createVersion === true;
            return [
              ...(shouldCreateVersion
                ? [
                    AgentsActions.createAgentVersion({
                      tenantUuid,
                      agentUuid: agent.agentUuid,
                    }),
                  ]
                : []),
              AgentsActions.updateAgentSuccess({ agent }),
            ];
          }),
          catchError((error) => of(AgentsActions.updateAgentFailure({ error, agentUuid }))),
        ),
      ),
    ),
  );

  refreshAgentAfterUpdate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AgentsActions.updateAgentSuccess),
      map(({ agent }) =>
        AgentsActions.getAgentDetail({
          tenantUuid: agent.tenantUuid,
          agentUuid: agent.agentUuid,
        }),
      ),
    ),
  );
  deleteInstruction$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AgentsActions.deleteInstruction),
      mergeMap(({ tenantUuid, agentUuid, agentInstructionId }) =>
        this.svc.deleteAgentInstruction(tenantUuid, agentUuid, agentInstructionId).pipe(
          map((agent) => {
            return AgentsActions.deleteInstructionSuccess({ agentInstructionId });
          }),
          catchError((error) => of(AgentsActions.deleteInstructionFailure({ error }))),
        ),
      ),
    ),
  );
  deletePrompts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AgentsActions.deletePrompt),
      mergeMap(({ tenantUuid, agentUuid, agentPromptId }) =>
        this.svc.deleteAgentPrompt(tenantUuid, agentUuid, agentPromptId).pipe(
          map((agent) => {
            return AgentsActions.deletePromptSuccess({ agentPromptId });
          }),
          catchError((error) => of(AgentsActions.deletePromptFailure({ error }))),
        ),
      ),
    ),
  );
  createAgent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AgentsActions.createAgent),
      switchMap(
        ({
          tenantUuid,
          createAgent,
          createInstructions,
          createPrompts,
          createInputs,
          createVersion,
        }) =>
          this.svc.createAgent(tenantUuid, createAgent).pipe(
            switchMap((agent) => {
              const obs: Observable<any>[] = [];

              // Instructions
              createInstructions.forEach((payload) =>
                obs.push(this.createInstructionInternal(tenantUuid, agent.agentUuid, payload)),
              );

              // Prompts
              createPrompts.forEach((payload) =>
                obs.push(this.svc.createAgentPrompts(tenantUuid, agent.agentUuid, payload)),
              );

              // Inputs
              if (createInputs.length) {
                obs.push(this.svc.createAgentInputs(tenantUuid, agent.agentUuid, createInputs));
              }

              const finalize = () => {
                if (createVersion) {
                  return this.svc
                    .createAgentVersion(tenantUuid, agent.agentUuid)
                    .pipe(map(() => agent));
                }
                return of(agent);
              };

              if (obs.length === 0) {
                return finalize();
              }

              return forkJoin(obs).pipe(switchMap(() => finalize()));
            }),
            map((agent) => AgentsActions.createAgentSuccess({ agent })),
            catchError((error) => of(AgentsActions.createAgentFailure({ error }))),
          ),
      ),
    ),
  );

  deleteAgent$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AgentsActions.deleteAgent),
      switchMap(({ tenantUuid, agentUuid }) =>
        this.svc.deleteAgent(tenantUuid, agentUuid).pipe(
          map((agent) => {
            return AgentsActions.deleteAgentSuccess({ agentUuid });
          }),
          catchError((error) => of(AgentsActions.deleteAgentFailure({ error, agentUuid }))),
        ),
      ),
    ),
  );
  getAgentVersions = createEffect(() =>
    this.actions$.pipe(
      ofType(AgentsActions.getAgentVersions),
      switchMap(({ tenantUuid, agentUuid }) =>
        this.svc.getAgentVersions(tenantUuid, agentUuid).pipe(
          map((agentVersions) => AgentsActions.getAgentVersionsSuccess({ agentVersions })),
          catchError((error) => of(AgentsActions.getAgentVersionsFailure({ error }))),
        ),
      ),
    ),
  );
  createAgentVersion$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AgentsActions.createAgentVersion),
      switchMap(({ tenantUuid, agentUuid }) =>
        this.svc.createAgentVersion(tenantUuid, agentUuid).pipe(
          map(() => {
            return AgentsActions.createAgentVersionSuccess({ tenantUuid, agentUuid });
          }),
          catchError((error) => of(AgentsActions.createAgentVersionFailure({ error }))),
        ),
      ),
    ),
  );
  updateAgentVersionStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AgentsActions.updateAgentVersionStatus),
      switchMap(({ tenantUuid, agentUuid, versionId, status }) =>
        this.svc.updateAgentVersionStatus(tenantUuid, agentUuid, versionId, status).pipe(
          map(() => {
            return AgentsActions.updateAgentVersionStatusSuccess({ tenantUuid, agentUuid });
          }),
          catchError((error) => of(AgentsActions.createAgentVersionFailure({ error }))),
        ),
      ),
    ),
  );
  private readonly store = inject(Store);
  getAgentsAfterChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        AgentsActions.updateAgentSuccess,
        AgentsActions.updateAgentVersionStatusSuccess,
        AgentsActions.updateAgentEverythingSuccess,
      ),
      withLatestFrom(this.store.select(selectSelectedSubscription)),
      filter(([, sub]) => !!sub),
      map(([, sub]) =>
        AgentsActions.getAgents({
          tenantUuid: (sub as any).tenantUuid,
          subscriptionUuid: (sub as any).subscriptionUuid,
        }),
      ),
    ),
  );
  getAgentsInstructionsAfterChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        AgentsActions.createInstructionSuccess,
        AgentsActions.updateInstructionSuccess,
        AgentsActions.deleteInstructionSuccess,
        AgentsActions.updateAgentEverythingSuccess,
      ),
      withLatestFrom(this.store.select(selectSelectedAgent)),
      filter(([, sub]) => !!sub),
      map(([, sub]) =>
        AgentsActions.getInstructionDetail({
          tenantUuid: (sub as any).tenantUuid,
          agentUuid: (sub as any).agentUuid,
        }),
      ),
    ),
  );

  getAgentsInputsAfterChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        AgentsActions.createAgentInputSuccess,
        AgentsActions.updateAgentInputSuccess,
        AgentsActions.deleteAgentInputSuccess,
        AgentsActions.updateAgentEverythingSuccess,
      ),
      withLatestFrom(this.store.select(selectSelectedAgent)),
      filter(([, sub]) => !!sub),
      map(([, sub]) =>
        AgentsActions.getAgentInputs({
          tenantUuid: (sub as any).tenantUuid,
          agentUuid: (sub as any).agentUuid,
        }),
      ),
    ),
  );

  getAgentHubsAfterChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        AgentsActions.createAgentSuccess,
        AgentsActions.updateAgentSuccess,
        AgentsActions.deleteAgentSuccess,
        AgentsActions.updateAgentEverythingSuccess,
      ),
      withLatestFrom(this.store.select(selectSelectedAgent)),
      filter(([, sub]) => !!sub),
      map(([, sub]) =>
        AgentsActions.getAgentHubs({
          tenantUuid: (sub as any).tenantUuid,
        }),
      ),
    ),
  );

  getAgentPromptsAfterChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        AgentsActions.createPromptSuccess,
        AgentsActions.updatePromptSuccess,
        AgentsActions.deletePromptSuccess,
        AgentsActions.updateAgentEverythingSuccess,
      ),
      withLatestFrom(this.store.select(selectSelectedAgent)),
      filter(([, sub]) => !!sub),
      map(([, sub]) =>
        AgentsActions.getPrompts({
          tenantUuid: (sub as any).tenantUuid,
          agentUuid: (sub as any).agentUuid,
        }),
      ),
    ),
  );
  private readonly gcsUploadService = inject(GcsUploadService);
  updateAgentInstruction$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AgentsActions.updateInstruction),
      mergeMap(({ tenantUuid, agentUuid, agentInstructionId, changes }) => {
        const { file, currentFile } = changes;

        const agentInstructionDTO: CreateUpdateAgentInstructionPayload = {
          title: changes.title,
          body: changes.body,
          displayOrder: changes.displayOrder,
        };

        if (file) {
          agentInstructionDTO['file'] = {
            fileName: file.fileName,
            contentType: file.contentType,
            size: file.size,
            resumable: true,
          };
        } else {
          if (!currentFile) {
            agentInstructionDTO['deleteFile'] = true;
          }
        }

        return this.svc
          .updateAgentInstruction(tenantUuid, agentUuid, agentInstructionId, agentInstructionDTO)
          .pipe(
            switchMap((agentInstruction: any) => {
              const signedUrl = agentInstruction?.file?.url?.url;

              if (signedUrl && file) {
                return this.gcsUploadService.initiateResumableUpload(file.file!, signedUrl).pipe(
                  switchMap((uploadUrl) =>
                    this.gcsUploadService.uploadFileData(uploadUrl, file.file!),
                  ),
                  switchMap((progress) => {
                    if (progress === 100) {
                      return this.svc.confirmAgentInstructionFile(
                        tenantUuid,
                        agentUuid,
                        agentInstructionId,
                      );
                    }
                    return [];
                  }),
                  map(() => AgentsActions.updateInstructionSuccess({ agentInstruction })),
                  catchError((error) => of(AgentsActions.updateInstructionFailure({ error }))),
                );
              }
              return of(
                AgentsActions.updateInstructionSuccess({
                  agentInstruction,
                }),
              );
            }),
            catchError((error) => of(AgentsActions.updateInstructionFailure({ error }))),
          );
      }),
    ),
  );
  createAgentInstruction$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AgentsActions.createInstruction),
      mergeMap(({ tenantUuid, agentUuid, changes, createVersion }) => {
        const { file } = changes;

        const agentInstructionDTO: CreateUpdateAgentInstructionPayload = {
          title: changes.title,
          body: changes.body,
          displayOrder: changes.displayOrder,
        };

        if (file) {
          agentInstructionDTO['file'] = {
            fileName: file.fileName,
            contentType: file.contentType,
            size: file.size,
            resumable: true,
          };
        }

        return this.svc.createAgentInstruction(tenantUuid, agentUuid, agentInstructionDTO).pipe(
          switchMap((agentInstruction) => {
            const signedUrl = agentInstruction?.file?.url?.url;

            if (signedUrl && file) {
              return this.gcsUploadService.initiateResumableUpload(file.file!, signedUrl).pipe(
                switchMap((uploadUrl) =>
                  this.gcsUploadService.uploadFileData(uploadUrl, file.file!),
                ),
                switchMap((progress) => {
                  if (progress === 100) {
                    return this.svc.confirmAgentInstructionFile(
                      tenantUuid,
                      agentUuid,
                      agentInstruction.agentInstructionId,
                    );
                  }
                  return [];
                }),
                map(() =>
                  AgentsActions.createInstructionSuccess({
                    agentInstruction,
                    createVersion,
                  }),
                ),
                catchError((error) => of(AgentsActions.createInstructionFailure({ error }))),
              );
            }
            return of(
              AgentsActions.createInstructionSuccess({
                agentInstruction,
                createVersion,
              }),
            );
          }),
          catchError((error) => of(AgentsActions.createInstructionFailure({ error }))),
        );
      }),
    ),
  );
  updateAgentEverything$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AgentsActions.updateAgentEverything),
      switchMap(
        ({ tenantUuid, agentUuid, agentChanges, instructions, prompts, inputs, createVersion }) => {
          return this.svc.updateAgent(tenantUuid, agentUuid, agentChanges).pipe(
            switchMap((agent) => {
              const obs: Observable<any>[] = [];

              // Instructions
              instructions.delete.forEach((id) =>
                obs.push(this.svc.deleteAgentInstruction(tenantUuid, agentUuid, id)),
              );
              instructions.create.forEach((payload) =>
                obs.push(this.createInstructionInternal(tenantUuid, agentUuid, payload)),
              );
              instructions.update.forEach((u) =>
                obs.push(
                  this.updateInstructionInternal(
                    tenantUuid,
                    agentUuid,
                    u.agentInstructionId,
                    u.changes,
                  ),
                ),
              );

              // Prompts
              prompts.delete.forEach((id) =>
                obs.push(this.svc.deleteAgentPrompt(tenantUuid, agentUuid, id)),
              );
              prompts.create.forEach((payload) =>
                obs.push(this.svc.createAgentPrompts(tenantUuid, agentUuid, payload)),
              );
              prompts.update.forEach((u) =>
                obs.push(
                  this.svc.updateAgentPrompts(tenantUuid, agentUuid, u.agentPromptId, u.changes),
                ),
              );

              // Inputs
              inputs.delete.forEach((id) =>
                obs.push(this.svc.deleteAgentInput(tenantUuid, agentUuid, id)),
              );
              if (inputs.create.length) {
                obs.push(this.svc.createAgentInputs(tenantUuid, agentUuid, inputs.create));
              }
              inputs.update.forEach((u) =>
                obs.push(
                  this.svc.updateAgentInput(tenantUuid, agentUuid, u.agentInputId, u.changes),
                ),
              );

              const finalize = () => {
                if (createVersion) {
                  return this.svc
                    .createAgentVersion(tenantUuid, agentUuid)
                    .pipe(map(() => ({ agent, createVersion })));
                }
                return of({ agent, createVersion });
              };

              if (obs.length === 0) {
                return finalize();
              }

              return forkJoin(obs).pipe(switchMap(() => finalize()));
            }),
            map(({ agent, createVersion }) =>
              AgentsActions.updateAgentEverythingSuccess({ agent, createVersion }),
            ),
            catchError((error) => of(AgentsActions.updateAgentEverythingFailure({ error }))),
          );
        },
      ),
    ),
  );

  private createInstructionInternal(
    tenantUuid: string,
    agentUuid: string,
    changes: CreateUpdateAgentInstructionPayload,
  ): Observable<any> {
    const { file } = changes;
    const agentInstructionDTO: CreateUpdateAgentInstructionPayload = {
      title: changes.title,
      body: changes.body,
      displayOrder: changes.displayOrder,
    };

    if (file) {
      agentInstructionDTO['file'] = {
        fileName: file.fileName,
        contentType: file.contentType,
        size: file.size,
        resumable: true,
      };
    }

    return this.svc.createAgentInstruction(tenantUuid, agentUuid, agentInstructionDTO).pipe(
      switchMap((agentInstruction) => {
        const signedUrl = agentInstruction?.file?.url?.url;
        if (signedUrl && file) {
          return this.uploadFileInternal(
            tenantUuid,
            agentUuid,
            agentInstruction.agentInstructionId,
            file.file!,
            signedUrl,
          );
        }
        return of(agentInstruction);
      }),
    );
  }

  private updateInstructionInternal(
    tenantUuid: string,
    agentUuid: string,
    agentInstructionId: number,
    changes: CreateUpdateAgentInstructionPayload,
  ): Observable<any> {
    const { file, currentFile } = changes;
    const agentInstructionDTO: CreateUpdateAgentInstructionPayload = {
      title: changes.title,
      body: changes.body,
      displayOrder: changes.displayOrder,
    };

    if (file) {
      agentInstructionDTO['file'] = {
        fileName: file.fileName,
        contentType: file.contentType,
        size: file.size,
        resumable: true,
      };
    } else if (!currentFile) {
      agentInstructionDTO['deleteFile'] = true;
    }

    return this.svc
      .updateAgentInstruction(tenantUuid, agentUuid, agentInstructionId, agentInstructionDTO)
      .pipe(
        switchMap((agentInstruction) => {
          const signedUrl = agentInstruction?.file?.url?.url;
          if (signedUrl && file) {
            return this.uploadFileInternal(
              tenantUuid,
              agentUuid,
              agentInstructionId,
              file.file!,
              signedUrl,
            );
          }
          return of(agentInstruction);
        }),
      );
  }

  private uploadFileInternal(
    tenantUuid: string,
    agentUuid: string,
    agentInstructionId: number,
    file: File,
    signedUrl: string,
  ): Observable<any> {
    return this.gcsUploadService.initiateResumableUpload(file, signedUrl).pipe(
      switchMap((uploadUrl) => this.gcsUploadService.uploadFileData(uploadUrl, file)),
      switchMap((progress) => {
        if (progress === 100) {
          return this.svc.confirmAgentInstructionFile(tenantUuid, agentUuid, agentInstructionId);
        }
        return of(null);
      }),
    );
  }
}
